import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import { Card, Row, Col, Table, Spinner } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';

import { objectShowNumber } from '@app/utils/define';

import {
  baoCaoGetChiTietNhanVienAction,
  baoCaoClearChiTiet,
} from '@app/store/reducers/baocao';

import { URI_INTERNAL } from '@app/utils/pathLocations';

import {
  TRANG_THAI_CONG_VIEC_OPTIONS,
  TRANG_THAI_KHAC_OPTIONS,
  TRANG_THAI_YEU_CAU_OPTIONS,
  TRANG_THAI_HO_TRO_OPTIONS,
  MUC_DO_UU_TIEN,
  MUC_DO_PHUC_TAP_OPTIONS,
  LOAI_LOI_OPTIONS,
  mapMucDoUuTienLabel,
  mapTrangThaiCongViecLabel,
  mapTrangThaiKhacLabel,
  mapTrangThaiYeuCauLabel,
  mapTrangThaiHoTroLabel,
  mapMucDoPhucTapLabel,
  mapLoaiLoiLabel,
} from '@app/store/constants';

import { rangeDateReport, messageToast } from '@app/utils/define';

import { linhVucGetAllAction } from '@store/reducers/linhvuc';
import { khoaGetAllAction } from '@store/reducers/khoa';
import { nguoiPhoiHopGetAllAction } from '@store/reducers/nguoiphoihop';

const getQueryInt = (search, key, defaultValue = 0) => {
  try {
    const sp = new URLSearchParams(search || '');
    const raw = sp.get(key);
    const n = Number(raw);
    return Number.isFinite(n) ? n : defaultValue;
  } catch {
    return defaultValue;
  }
};

const getQueryStr = (search, key, defaultValue = '') => {
  try {
    const sp = new URLSearchParams(search || '');
    return sp.get(key) || defaultValue;
  } catch {
    return defaultValue;
  }
};

const getSafeReturnUrl = (search) => {
  const raw = getQueryStr(search, 'returnUrl', '');
  if (!raw) return '';

  if (typeof raw === 'string' && raw.startsWith('/')) {
    return raw;
  }

  try {
    const decoded = decodeURIComponent(raw);
    if (decoded && decoded.startsWith('/')) {
      return decoded;
    }
  } catch {}

  return '';
};

const TABLE_HEADER_FOOTER_CELL_STYLE = {
  textAlign: 'center',
  verticalAlign: 'middle',
};

const safeText = (v) => (v == null ? '' : String(v));

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('vi-VN');
};

const RANGE_CUSTOM = 'Custom Range';
const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return { from: toYMD(pair[0]), to: toYMD(pair[1]) };
};

const detectRangeLabel = (from, to) => {
  if (!from && !to) return '';
  for (const lbl of RANGE_OPTIONS) {
    if (lbl === RANGE_CUSTOM) continue;
    const r = getRangeFromTo(lbl);
    if (r.from === from && r.to === to) return lbl;
  }
  return RANGE_CUSTOM;
};

const pickName = (obj) =>
  obj?.name ||
  obj?.ten ||
  obj?.tenKhoa ||
  obj?.tenLinhVuc ||
  obj?.title ||
  obj?.label ||
  '';

const getTicketDisplay = (url) => {
  if (!url) return '';
  const match = url.match(/github\.com\/.+\/issues\/(\d+)/);
  if (match) return match[1];
  return 'Link';
};

const renderTicketLink = (url) => {
  if (!url) return '';
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
      {getTicketDisplay(url)}
    </a>
  );
};

const COLUMN_STYLE_MAP = {
  canlam: { headerBg: '#e1e5f2', headerText: '#333333', border: '#c4ccdd' },
  danglam: { headerBg: '#d3e5ff', headerText: '#245b8f', border: '#aac6ff' },
  choduyet: { headerBg: '#ffe0b2', headerText: '#8f6a24', border: '#ffc875' },
  hoanthanh: { headerBg: '#cef4e4', headerText: '#247a55', border: '#a0e0c6' },
};

const badgeDefault = {
  backgroundColor: '#e9ecef',
  border: '1px solid #ced4da',
  color: '#495057',
  borderRadius: '12px',
  padding: '2px 8px',
  fontSize: '0.85rem',
  display: 'inline-block',
  minWidth: '90px',
  textAlign: 'center',
};

const toCongViecStatusKey = (status) => {
  const opt = TRANG_THAI_CONG_VIEC_OPTIONS.find((x) => x.value === status || x.key === status);
  return opt ? opt.key : null;
};

const getStatusStyleProject = (status) => {
  const key = toCongViecStatusKey(status);
  const style = key ? COLUMN_STYLE_MAP[key] : null;
  if (!style) return badgeDefault;

  return {
    ...badgeDefault,
    backgroundColor: style.headerBg,
    border: `1px solid ${style.border}`,
    color: style.headerText,
  };
};

const getEffectiveTienDoProjectTask = (item) => {
  const key = toCongViecStatusKey(item?.status);
  if (key === 'hoanthanh') return 100;
  return item?.tienDo ?? null;
};

const STATUS_COLOR_MAP_KHAC = {
  chuabatdau: { bg: '#e1e5f2', text: '#333333', border: '#c4ccdd' },
  dangthuchien: { bg: '#d3e5ff', text: '#245b8f', border: '#aac6ff' },
  hoanthanh: { bg: '#cef4e4', text: '#247a55', border: '#a0e0c6' },
};

const toTrangThaiKhacKey = (valueOrKey) => {
  const opt =
    TRANG_THAI_KHAC_OPTIONS.find((o) => o.value === valueOrKey || o.key === valueOrKey) ||
    TRANG_THAI_KHAC_OPTIONS.find((o) => o.value === Number(valueOrKey));
  return opt ? opt.key : null;
};

const getStatusStyleNhiemVuKhac = (status) => {
  const key = toTrangThaiKhacKey(status);
  const style = key ? STATUS_COLOR_MAP_KHAC[key] : null;
  if (!style) return badgeDefault;

  return {
    ...badgeDefault,
    backgroundColor: style.bg,
    border: `1px solid ${style.border}`,
    color: style.text,
  };
};

const STATUS_COLOR_MAP_YEUCAU = {
  chophanhoi: { bg: '#e1e5f2', text: '#333333', border: '#c4ccdd' },
  dangtest: { bg: '#d3e5ff', text: '#245b8f', border: '#aac6ff' },
  chonangcap: { bg: '#ffe0b2', text: '#8f6a24', border: '#ffc875' },
  hoanthanh: { bg: '#cef4e4', text: '#247a55', border: '#a0e0c6' },
};

const toTrangThaiYeuCauKey = (valueOrKey) => {
  const opt =
    TRANG_THAI_YEU_CAU_OPTIONS.find((o) => o.value === valueOrKey || o.key === valueOrKey) ||
    TRANG_THAI_YEU_CAU_OPTIONS.find((o) => o.value === Number(valueOrKey));
  return opt ? opt.key : null;
};

const getStatusStyleYeuCau = (status) => {
  const key = toTrangThaiYeuCauKey(status);
  const style = key ? STATUS_COLOR_MAP_YEUCAU[key] : null;
  if (!style) return badgeDefault;

  return {
    ...badgeDefault,
    backgroundColor: style.bg,
    border: `1px solid ${style.border}`,
    color: style.text,
  };
};

const STATUS_COLOR_MAP_HOTRO = {
  dangthuchien: { bg: '#d3e5ff', text: '#245b8f', border: '#aac6ff' },
  hoanthanh: { bg: '#cef4e4', text: '#247a55', border: '#a0e0c6' },
};

const toTrangThaiHoTroKey = (valueOrKey) => {
  const opt =
    TRANG_THAI_HO_TRO_OPTIONS.find((o) => o.value === valueOrKey || o.key === valueOrKey) ||
    TRANG_THAI_HO_TRO_OPTIONS.find((o) => o.value === Number(valueOrKey));
  return opt ? opt.key : null;
};

const getStatusStyleHoTro = (status) => {
  const key = toTrangThaiHoTroKey(status);
  const style = key ? STATUS_COLOR_MAP_HOTRO[key] : null;
  if (!style) return badgeDefault;

  return {
    ...badgeDefault,
    backgroundColor: style.bg,
    border: `1px solid ${style.border}`,
    color: style.text,
  };
};

const buildFilterPayload = ({
  nhanVienId,
  from,
  to,
  projectStatusFilter,
  projectPriorityFilter,
  otherStatusFilter,
  otherPriorityFilter,
  yeuCauStatusFilter,
  yeuCauPriorityFilter,
  hoTroStatusFilter,
  hoTroLoaiLoiFilter,
  hoTroMucDoFilter,
}) => ({
  NhanVienId: nhanVienId,

  From: from ? String(from) : null,
  To: to ? String(to) : null,

  CongViecStatus: projectStatusFilter !== '' ? Number(projectStatusFilter) : null,
  CongViecMucDo: projectPriorityFilter !== '' ? Number(projectPriorityFilter) : null,

  NhiemVuKhacStatus: otherStatusFilter !== '' ? Number(otherStatusFilter) : null,
  NhiemVuKhacMucDo: otherPriorityFilter !== '' ? Number(otherPriorityFilter) : null,

  YeuCauStatus: yeuCauStatusFilter !== '' ? Number(yeuCauStatusFilter) : null,
  YeuCauMucDo: yeuCauPriorityFilter !== '' ? Number(yeuCauPriorityFilter) : null,

  HoTroStatus: hoTroStatusFilter !== '' ? Number(hoTroStatusFilter) : null,
  HoTroLoaiLoi: hoTroLoaiLoiFilter !== '' ? Number(hoTroLoaiLoiFilter) : null,
  HoTroMucDoPhucTap: hoTroMucDoFilter !== '' ? Number(hoTroMucDoFilter) : null,
});

const headerGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
};

const totalCountStyleInline = {
  justifySelf: 'center',
  fontWeight: 700,
  color: '#dc3545',
  textAlign: 'center',
};

const getKhoaCountByIds = (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return 0;
  const uniq = new Set(ids.filter((x) => x != null));
  return uniq.size;
};

const BaoCaoChiTietNhanVien = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const suppressAutoFetchRef = useRef(false);

  const { chiTietNhanVien, loadingChiTietNhanVien, errorChiTietNhanVien } =
    useSelector((s) => s.baocao || {});

  const khoaState = useSelector((state) => state.khoa || {});
  const linhVucState = useSelector((state) => state.linhvuc || {});
  const nguoiPhoiHopState = useSelector((state) => state.nguoiPhoiHop || {});

  const khoaList = Array.isArray(khoaState.listKhoa) ? khoaState.listKhoa : [];
  const linhVucList = Array.isArray(linhVucState.listLinhVuc) ? linhVucState.listLinhVuc : [];
  const nguoiPhoiHopList = Array.isArray(nguoiPhoiHopState.listNguoiPhoiHop)
    ? nguoiPhoiHopState.listNguoiPhoiHop
    : [];

  const teamId = useMemo(() => getQueryInt(location.search, 'teamId', 0), [location.search]);
  const nhanVienId = useMemo(() => getQueryInt(location.search, 'nhanVienId', 0), [location.search]);
  const tenTeam = useMemo(() => getQueryStr(location.search, 'tenTeam', ''), [location.search]);

  const sourceTab = useMemo(
    () => getQueryStr(location.search, 'sourceTab', getQueryStr(location.search, 'source', '')),
    [location.search]
  );

  const returnUrl = useMemo(() => getSafeReturnUrl(location.search), [location.search]);

  const [filterTab, setFilterTab] = useState('all');

  const [projectStatusFilter, setProjectStatusFilter] = useState('');
  const [projectPriorityFilter, setProjectPriorityFilter] = useState('');

  const [otherStatusFilter, setOtherStatusFilter] = useState('');
  const [otherPriorityFilter, setOtherPriorityFilter] = useState('');

  const [yeuCauStatusFilter, setYeuCauStatusFilter] = useState('');
  const [yeuCauPriorityFilter, setYeuCauPriorityFilter] = useState('');

  const [hoTroStatusFilter, setHoTroStatusFilter] = useState('');
  const [hoTroLoaiLoiFilter, setHoTroLoaiLoiFilter] = useState('');
  const [hoTroMucDoFilter, setHoTroMucDoFilter] = useState('');

  const [pageProject, setPageProject] = useState(1);
  const [pageSizeProject, setPageSizeProject] = useState(20);

  const [pageOther, setPageOther] = useState(1);
  const [pageSizeOther, setPageSizeOther] = useState(20);

  const [pageYeuCau, setPageYeuCau] = useState(1);
  const [pageSizeYeuCau, setPageSizeYeuCau] = useState(20);

  const [pageHoTro, setPageHoTro] = useState(1);
  const [pageSizeHoTro, setPageSizeHoTro] = useState(20);

  const queryFrom = useMemo(() => getQueryStr(location.search, 'From', ''), [location.search]);
  const queryTo = useMemo(() => getQueryStr(location.search, 'To', ''), [location.search]);

  const updateQuery = useCallback(
    (patch = {}) => {
      const sp = new URLSearchParams(location.search || '');

      if (teamId != null) sp.set('teamId', String(teamId));
      if (nhanVienId != null) sp.set('nhanVienId', String(nhanVienId));
      if (tenTeam) sp.set('tenTeam', String(tenTeam));

      if (!patch.From) sp.delete('From');
      else sp.set('From', String(patch.From));

      if (!patch.To) sp.delete('To');
      else sp.set('To', String(patch.To));

      const qs = sp.toString();
      history.replace({
        pathname: location.pathname,
        search: qs ? `?${qs}` : '',
      });
    },
    [history, location.pathname, location.search, teamId, nhanVienId, tenTeam]
  );

  const [rangeLabel, setRangeLabel] = useState(''); // '' = tất cả
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const resetAllPages = useCallback(() => {
    setPageProject(1);
    setPageOther(1);
    setPageYeuCau(1);
    setPageHoTro(1);
  }, []);

  const clearDateFilter = useCallback(() => {
    setRangeLabel('');
    setDraftFrom('');
    setDraftTo('');
    setAppliedFrom('');
    setAppliedTo('');
    resetAllPages();

    updateQuery({ From: '', To: '' });
  }, [resetAllPages, updateQuery]);

  const onChangeRangeLabel = useCallback(
    (lbl) => {
      if (!lbl) {
        clearDateFilter();
        return;
      }

      setRangeLabel(lbl);

      if (lbl !== RANGE_CUSTOM) {
        const r = getRangeFromTo(lbl);

        setDraftFrom(r.from);
        setDraftTo(r.to);
        setAppliedFrom(r.from);
        setAppliedTo(r.to);

        resetAllPages();
        updateQuery({ From: r.from, To: r.to });
        return;
      }

      setDraftFrom(appliedFrom || queryFrom || '');
      setDraftTo(appliedTo || queryTo || '');
    },
    [appliedFrom, appliedTo, queryFrom, queryTo, clearDateFilter, resetAllPages, updateQuery]
  );

  const applyCustomRange = useCallback(() => {
    if (rangeLabel === RANGE_CUSTOM && !draftFrom && !draftTo) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }

    let f = draftFrom || '';
    let t = draftTo || '';

    if (f && t && t < f) {
      const tmp = f;
      f = t;
      t = tmp;
    }

    setAppliedFrom(f);
    setAppliedTo(t);
    resetAllPages();

    updateQuery({ From: f, To: t });
  }, [rangeLabel, draftFrom, draftTo, resetAllPages, updateQuery]);

  const didInitRef = useRef(false);


  const backToBang2 = useMemo(() => {
    if (returnUrl) return returnUrl;

    const qs = new URLSearchParams();
    qs.set('teamId', String(teamId || 0));
    if (tenTeam) qs.set('tenTeam', tenTeam);

    if (queryFrom) qs.set('From', queryFrom);
    if (queryTo) qs.set('To', queryTo);

    if (
      sourceTab === 'bao-cao-thong-ke' ||
      sourceTab === 'thanh-vien-team' ||
      sourceTab === 'bao-cao-nhan-vien' ||
      sourceTab === 'tong-hop-tat-ca'
    ) {
      return `${URI_INTERNAL.BAOCAO_TONG_QUAN_NHAN_VIEN_PATH}?${qs.toString()}`;
    }

    return `${URI_INTERNAL.BAOCAO_THONGKE_PATH}${qs.toString() ? `?${qs.toString()}` : ''}`;
  }, [returnUrl, sourceTab, teamId, tenTeam, queryFrom, queryTo]);


  const getLinhVucNameById = (id) => {
    if (id == null) return '';
    if (!Array.isArray(linhVucList) || linhVucList.length === 0) return '';
    const found = linhVucList.find((x) => x?.id === id);
    return pickName(found) || '';
  };

  const getKhoaCodesByIds = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return '';
    if (!Array.isArray(khoaList) || khoaList.length === 0) return '';

    const codes = ids
      .map((kid) => khoaList.find((k) => k?.id === kid)?.maKhoa)
      .filter(Boolean);

    return codes.join(', ');
  };

  const getNguoiPhoiHopNamesByIds = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return '';
    if (!Array.isArray(nguoiPhoiHopList) || nguoiPhoiHopList.length === 0) return '';

    return ids
      .map((pid) => nguoiPhoiHopList.find((p) => p?.id === pid)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const fetchData = useCallback(
    (payload) => {
      if (!payload?.NhanVienId || payload.NhanVienId <= 0) return;
      dispatch(baoCaoGetChiTietNhanVienAction({ filter: payload }));
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(linhVucGetAllAction());
    dispatch(khoaGetAllAction());
    dispatch(nguoiPhoiHopGetAllAction());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(baoCaoClearChiTiet());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!nhanVienId || nhanVienId <= 0) {
      toast.error('Thiếu nhanVienId để xem Bảng 3.');
      history.push(backToBang2);
      return;
    }

    didInitRef.current = false;

    suppressAutoFetchRef.current = true;

    setProjectStatusFilter('');
    setProjectPriorityFilter('');
    setOtherStatusFilter('');
    setOtherPriorityFilter('');
    setYeuCauStatusFilter('');
    setYeuCauPriorityFilter('');
    setHoTroStatusFilter('');
    setHoTroLoaiLoiFilter('');
    setHoTroMucDoFilter('');

    const f = queryFrom || '';
    const t = queryTo || '';

    setAppliedFrom(f);
    setAppliedTo(t);
    setDraftFrom(f);
    setDraftTo(t);
    setRangeLabel(detectRangeLabel(f, t));

    resetAllPages();

    fetchData(
      buildFilterPayload({
        nhanVienId,
        from: f,
        to: t,
        projectStatusFilter: '',
        projectPriorityFilter: '',
        otherStatusFilter: '',
        otherPriorityFilter: '',
        yeuCauStatusFilter: '',
        yeuCauPriorityFilter: '',
        hoTroStatusFilter: '',
        hoTroLoaiLoiFilter: '',
        hoTroMucDoFilter: '',
      })
    );

    didInitRef.current = true;
  }, [nhanVienId, history, queryFrom, queryTo, resetAllPages, fetchData, backToBang2]);

  useEffect(() => {
    if (!didInitRef.current) return;

    const f = queryFrom || '';
    const t = queryTo || '';

    if (f === (appliedFrom || '') && t === (appliedTo || '')) return;

    suppressAutoFetchRef.current = true;

    setAppliedFrom(f);
    setAppliedTo(t);
    setDraftFrom(f);
    setDraftTo(t);
    setRangeLabel(detectRangeLabel(f, t));
    resetAllPages();

    fetchData(
      buildFilterPayload({
        nhanVienId,
        from: f,
        to: t,
        projectStatusFilter,
        projectPriorityFilter,
        otherStatusFilter,
        otherPriorityFilter,
        yeuCauStatusFilter,
        yeuCauPriorityFilter,
        hoTroStatusFilter,
        hoTroLoaiLoiFilter,
        hoTroMucDoFilter,
      })
    );
  }, [
    queryFrom,
    queryTo,
    nhanVienId,
    appliedFrom,
    appliedTo,
    resetAllPages,
    fetchData,
    projectStatusFilter,
    projectPriorityFilter,
    otherStatusFilter,
    otherPriorityFilter,
    yeuCauStatusFilter,
    yeuCauPriorityFilter,
    hoTroStatusFilter,
    hoTroLoaiLoiFilter,
    hoTroMucDoFilter,
  ]);

  useEffect(() => {
    if (!nhanVienId || nhanVienId <= 0) return;

    if (suppressAutoFetchRef.current) {
      suppressAutoFetchRef.current = false;
      return;
    }

    const payload = buildFilterPayload({
      nhanVienId,
      from: appliedFrom,
      to: appliedTo,
      projectStatusFilter,
      projectPriorityFilter,
      otherStatusFilter,
      otherPriorityFilter,
      yeuCauStatusFilter,
      yeuCauPriorityFilter,
      hoTroStatusFilter,
      hoTroLoaiLoiFilter,
      hoTroMucDoFilter,
    });

    fetchData(payload);
  }, [
    nhanVienId,
    appliedFrom,
    appliedTo,
    projectStatusFilter,
    projectPriorityFilter,
    otherStatusFilter,
    otherPriorityFilter,
    yeuCauStatusFilter,
    yeuCauPriorityFilter,
    hoTroStatusFilter,
    hoTroLoaiLoiFilter,
    hoTroMucDoFilter,
    fetchData,
  ]);

  useEffect(() => {
    if (!errorChiTietNhanVien) return;

    let msg =
      (errorChiTietNhanVien && errorChiTietNhanVien.message) ||
      (typeof errorChiTietNhanVien === 'string' ? errorChiTietNhanVien : '') ||
      (errorChiTietNhanVien && errorChiTietNhanVien.title) ||
      '';

    if (!msg && errorChiTietNhanVien && errorChiTietNhanVien.errors) {
      msg = Object.values(errorChiTietNhanVien.errors).flat().join('; ');
    }

    toast.error(msg || 'Không thể tải dữ liệu Bảng 3. Vui lòng thử lại.');
  }, [errorChiTietNhanVien]);

  const data = useMemo(() => {
    const x = chiTietNhanVien || {};
    const tenThanhVien = x.tenThanhVien ?? x.TenThanhVien ?? '';

    const projectTasksRaw = x.projectTasks ?? x.ProjectTasks ?? [];
    const otherTasksRaw = x.otherTasks ?? x.OtherTasks ?? [];
    const yeuCauTasksRaw = x.yeuCauTasks ?? x.YeuCauTasks ?? [];
    const hoTroTasksRaw = x.hoTroTasks ?? x.HoTroTasks ?? [];

    const projectTasks = Array.isArray(projectTasksRaw) ? projectTasksRaw : [];
    const otherTasks = Array.isArray(otherTasksRaw) ? otherTasksRaw : [];
    const yeuCauTasks = Array.isArray(yeuCauTasksRaw) ? yeuCauTasksRaw : [];
    const hoTroTasks = Array.isArray(hoTroTasksRaw) ? hoTroTasksRaw : [];

    return { tenThanhVien, projectTasks, otherTasks, yeuCauTasks, hoTroTasks };
  }, [chiTietNhanVien]);

  const sortedProjectTasks = useMemo(() => {
    return [...data.projectTasks].sort((a, b) => {
      const da = a.hanChot ?? a.HanChot ? new Date(a.hanChot ?? a.HanChot) : null;
      const db = b.hanChot ?? b.HanChot ? new Date(b.hanChot ?? b.HanChot) : null;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });
  }, [data.projectTasks]);

  const sortedOtherTasks = useMemo(() => {
    return [...data.otherTasks].sort((a, b) => {
      const da = a.hanChot ?? a.HanChot ? new Date(a.hanChot ?? a.HanChot) : null;
      const db = b.hanChot ?? b.HanChot ? new Date(b.hanChot ?? b.HanChot) : null;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });
  }, [data.otherTasks]);

  const sortedYeuCauTasks = useMemo(() => {
    return [...data.yeuCauTasks].sort((a, b) => {
      const da =
        a.ngayHoanThanhDuKien ?? a.NgayHoanThanhDuKien
          ? new Date(a.ngayHoanThanhDuKien ?? a.NgayHoanThanhDuKien)
          : null;
      const db =
        b.ngayHoanThanhDuKien ?? b.NgayHoanThanhDuKien
          ? new Date(b.ngayHoanThanhDuKien ?? b.NgayHoanThanhDuKien)
          : null;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });
  }, [data.yeuCauTasks]);

  const sortedHoTroTasks = useMemo(() => {
    return [...data.hoTroTasks].sort((a, b) => {
      const da = a.ngayHoTro ?? a.NgayHoTro ? new Date(a.ngayHoTro ?? a.NgayHoTro) : null;
      const db = b.ngayHoTro ?? b.NgayHoTro ? new Date(b.ngayHoTro ?? b.NgayHoTro) : null;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });
  }, [data.hoTroTasks]);

  const totalHoTroTheoKhoa = useMemo(() => {
    return sortedHoTroTasks.reduce((sum, raw) => {
      const ids = raw.khoaIds ?? raw.KhoaIds;
      return sum + getKhoaCountByIds(ids);
    }, 0);
  }, [sortedHoTroTasks]);

  // paging data
  const pagedProjectTasks = sortedProjectTasks.slice(
    (pageProject - 1) * pageSizeProject,
    pageProject * pageSizeProject
  );
  const pagedOtherTasks = sortedOtherTasks.slice(
    (pageOther - 1) * pageSizeOther,
    pageOther * pageSizeOther
  );
  const pagedYeuCauTasks = sortedYeuCauTasks.slice(
    (pageYeuCau - 1) * pageSizeYeuCau,
    pageYeuCau * pageSizeYeuCau
  );
  const pagedHoTroTasks = sortedHoTroTasks.slice(
    (pageHoTro - 1) * pageSizeHoTro,
    pageHoTro * pageSizeHoTro
  );

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-start mb-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => history.push(backToBang2)}
          title="Quay lại Bảng 2"
        >
          ← Quay lại
        </button>
      </div>

      <Card>
        <Card.Header>
          <div className="text-muted">
            Chi tiết công việc nhân viên
            {data.tenThanhVien ? (
              <>
                {' '}
                - <b>{data.tenThanhVien}</b>
              </>
            ) : null}
          </div>
        </Card.Header>

        <Card.Body>
          <div className="row mb-3">
            <div className="col-12 d-flex justify-content-start">
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className={`btn ${filterTab === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterTab('all')}
                >
                  Tất cả
                </button>
                <button
                  type="button"
                  className={`btn ${filterTab === 'dev' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterTab('dev')}
                >
                  Phát triển phần mềm
                </button>
                <button
                  type="button"
                  className={`btn ${filterTab === 'other' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterTab('other')}
                >
                  Nhiệm vụ khác
                </button>
                <button
                  type="button"
                  className={`btn ${filterTab === 'yeucau' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterTab('yeucau')}
                >
                  Xử lý yêu cầu
                </button>
                <button
                  type="button"
                  className={`btn ${filterTab === 'hotro' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterTab('hotro')}
                >
                  Hỗ trợ phần mềm
                </button>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            <div style={{ minWidth: 220 }}>
              <label className="form-label mb-1">Khoảng thời gian</label>
              <select
                className="form-select form-select-sm"
                value={rangeLabel}
                onChange={(e) => onChangeRangeLabel(e.target.value)}
              >
                <option value="">-- Chọn --</option>
                {RANGE_OPTIONS.map((lbl) => (
                  <option key={lbl} value={lbl}>
                    {lbl}
                  </option>
                ))}
              </select>
            </div>

            {rangeLabel === RANGE_CUSTOM && (
              <>
                <div>
                  <label className="form-label mb-1">Từ ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={draftFrom}
                    onChange={(e) => setDraftFrom(e.target.value)}
                  />
                </div>

                <div>
                  <label className="form-label mb-1">Đến ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={draftTo}
                    onChange={(e) => setDraftTo(e.target.value)}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={applyCustomRange}
                >
                  Lọc
                </button>
              </>
            )}

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={clearDateFilter}
              disabled={!appliedFrom && !appliedTo && !rangeLabel}
            >
              Xóa lọc
            </button>

            {(appliedFrom || appliedTo) && (
              <div className="filter-hint" style={{ width: '100%' }}>
                Đang lọc: {fmtDDMMYYYY(appliedFrom) || '...'} → {fmtDDMMYYYY(appliedTo) || '...'}
              </div>
            )}
          </div>

          <div className="position-relative">
            {loadingChiTietNhanVien && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.55)',
                }}
              >
                <Spinner animation="border" role="status" />
              </div>
            )}

            <>
              {/* DEV */}
              {(filterTab === 'all' || filterTab === 'dev') && (
                <Row>
                  <Col md={12}>
                    <Card className="mb-4">
                      <Card.Header style={headerGridStyle}>
                        <h3 className="card-title mb-0" style={{ justifySelf: 'start' }}>
                          Công việc Phát triển phần mềm
                        </h3>

                        <span style={totalCountStyleInline}>Tổng: {sortedProjectTasks.length}</span>

                        <div />
                      </Card.Header>

                      <Card.Body>
                        <div className="d-flex justify-content-between mb-2 flex-wrap">
                          <form className="form-inline mb-2">
                            <label className="mr-2">Hiển thị :</label>
                            <select
                              className="form-control"
                              value={pageSizeProject}
                              onChange={(e) => {
                                const newSize = parseInt(e.target.value, 10) || 20;
                                setPageSizeProject(newSize);
                                setPageProject(1);
                              }}
                            >
                              {objectShowNumber.map((item, key) => (
                                <option key={key} value={item.value}>
                                  {item.text}
                                </option>
                              ))}
                            </select>
                          </form>

                          <div className="form-inline mb-2">
                            <label className="mr-2">Trạng thái:</label>
                            <select
                              className="form-control mr-2"
                              value={projectStatusFilter}
                              onChange={(e) => {
                                setProjectStatusFilter(e.target.value);
                                setPageProject(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {TRANG_THAI_CONG_VIEC_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <label className="mr-2">Mức độ:</label>
                            <select
                              className="form-control"
                              value={projectPriorityFilter}
                              onChange={(e) => {
                                setProjectPriorityFilter(e.target.value);
                                setPageProject(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {MUC_DO_UU_TIEN.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm ml-2"
                              onClick={() => {
                                setProjectStatusFilter('');
                                setProjectPriorityFilter('');
                                setPageProject(1);
                              }}
                            >
                              Xóa lọc
                            </button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <Table striped bordered hover className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th style={{ ...TABLE_HEADER_FOOTER_CELL_STYLE, width: 70 }}>STT</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tên công việc</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Dự án</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Bước</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Mức độ</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Trạng thái</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tiến độ</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ngày giao</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Hạn chót</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedProjectTasks.length === 0 && (
                                <tr>
                                  <td colSpan={9} className="text-center text-muted">
                                    Không có công việc dự án.
                                  </td>
                                </tr>
                              )}

                              {pagedProjectTasks.map((raw, index) => {
                                const item = {
                                  title: raw.title ?? raw.Title,
                                  projectName: raw.projectName ?? raw.ProjectName,
                                  projectStepName: raw.projectStepName ?? raw.ProjectStepName,
                                  mucDo: raw.mucDo ?? raw.MucDo,
                                  status: raw.status ?? raw.Status,
                                  tienDo: raw.tienDo ?? raw.TienDo,
                                  ngayGiao:
                                    raw.ngayGiao ??
                                    raw.NgayGiao ??
                                    raw.ngayBatDau ??
                                    raw.NgayBatDau,
                                  hanChot: raw.hanChot ?? raw.HanChot,
                                };

                                const td = getEffectiveTienDoProjectTask(item);

                                return (
                                  <tr key={`${item.projectName || ''}-${item.title || ''}-${index}`}>
                                    <td className="text-center">
                                      {index + 1 + (pageProject - 1) * pageSizeProject}
                                    </td>
                                    <td className="font-weight-bold">{safeText(item.title)}</td>
                                    <td>{safeText(item.projectName)}</td>
                                    <td>{safeText(item.projectStepName)}</td>
                                    <td>{mapMucDoUuTienLabel(item.mucDo)}</td>
                                    <td>
                                      <span style={getStatusStyleProject(item.status)}>
                                        {mapTrangThaiCongViecLabel(item.status)}
                                      </span>
                                    </td>
                                    <td className="text-center">{td != null ? `${td}%` : ''}</td>
                                    <td>{formatDate(item.ngayGiao)}</td>
                                    <td>{formatDate(item.hanChot)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>

                        <div className="dataTables_paginate paging_simple_numbers mt-2">
                          <Pagination
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination pagination-sm no-margin pull-right"
                            activePage={pageProject}
                            itemsCountPerPage={Number(pageSizeProject)}
                            totalItemsCount={sortedProjectTasks.length}
                            pageRangeDisplayed={5}
                            onChange={(n) => setPageProject(n)}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* OTHER */}
              {(filterTab === 'all' || filterTab === 'other') && (
                <Row>
                  <Col md={12}>
                    <Card className="mb-4">
                      <Card.Header style={headerGridStyle}>
                        <h3 className="card-title mb-0" style={{ justifySelf: 'start' }}>
                          Nhiệm vụ khác
                        </h3>

                        <span style={totalCountStyleInline}>Tổng: {sortedOtherTasks.length}</span>

                        <div />
                      </Card.Header>

                      <Card.Body>
                        <div className="d-flex justify-content-between mb-2 flex-wrap">
                          <form className="form-inline mb-2">
                            <label className="mr-2">Hiển thị :</label>
                            <select
                              className="form-control"
                              value={pageSizeOther}
                              onChange={(e) => {
                                const newSize = parseInt(e.target.value, 10) || 20;
                                setPageSizeOther(newSize);
                                setPageOther(1);
                              }}
                            >
                              {objectShowNumber.map((item, key) => (
                                <option key={key} value={item.value}>
                                  {item.text}
                                </option>
                              ))}
                            </select>
                          </form>

                          <div className="form-inline mb-2">
                            <label className="mr-2">Trạng thái:</label>
                            <select
                              className="form-control mr-2"
                              value={otherStatusFilter}
                              onChange={(e) => {
                                setOtherStatusFilter(e.target.value);
                                setPageOther(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {TRANG_THAI_KHAC_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <label className="mr-2">Mức độ:</label>
                            <select
                              className="form-control"
                              value={otherPriorityFilter}
                              onChange={(e) => {
                                setOtherPriorityFilter(e.target.value);
                                setPageOther(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {MUC_DO_UU_TIEN.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm ml-2"
                              onClick={() => {
                                setOtherStatusFilter('');
                                setOtherPriorityFilter('');
                                setPageOther(1);
                              }}
                            >
                              Xóa lọc
                            </button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <Table striped bordered hover className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th style={{ ...TABLE_HEADER_FOOTER_CELL_STYLE, width: 70 }}>STT</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Nhiệm vụ</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Công việc con</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Mức độ</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Trạng thái</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Tiến độ</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ngày giao</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Hạn chót</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Người giao</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedOtherTasks.length === 0 && (
                                <tr>
                                  <td colSpan={9} className="text-center text-muted">
                                    Không có nhiệm vụ khác.
                                  </td>
                                </tr>
                              )}

                              {pagedOtherTasks.map((raw, index) => {
                                const item = {
                                  title: raw.title ?? raw.Title,
                                  mucDo: raw.mucDo ?? raw.MucDo,
                                  status: raw.status ?? raw.Status,
                                  tienDo: raw.tienDo ?? raw.TienDo,
                                  ngayGiao: raw.ngayGiao ?? raw.NgayGiao,
                                  hanChot: raw.hanChot ?? raw.HanChot,
                                  nguoiGiao: raw.nguoiGiao ?? raw.NguoiGiao,
                                  tongSoCongViecCon: raw.tongSoCongViecCon ?? raw.TongSoCongViecCon ?? 0,
                                  soCongViecConHoanThanh:
                                    raw.soCongViecConHoanThanh ?? raw.SoCongViecConHoanThanh ?? 0,
                                };

                                return (
                                  <tr key={`${item.title || ''}-${index}`}>
                                    <td className="text-center">
                                      {index + 1 + (pageOther - 1) * pageSizeOther}
                                    </td>
                                    <td>
                                      <div className="font-weight-bold">{safeText(item.title)}</div>
                                    </td>
                                    <td>
                                      <div className="text-muted small">
                                        {item.tongSoCongViecCon > 0
                                          ? `${item.soCongViecConHoanThanh}/${item.tongSoCongViecCon}`
                                          : '0/0'}
                                      </div>
                                    </td>
                                    <td>{mapMucDoUuTienLabel(item.mucDo)}</td>
                                    <td>
                                      <span style={getStatusStyleNhiemVuKhac(item.status)}>
                                        {mapTrangThaiKhacLabel(item.status)}
                                      </span>
                                    </td>
                                    <td>{item.tienDo != null ? `${item.tienDo}%` : ''}</td>
                                    <td>{formatDate(item.ngayGiao)}</td>
                                    <td>{formatDate(item.hanChot)}</td>
                                    <td>{safeText(item.nguoiGiao)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>

                        <div className="dataTables_paginate paging_simple_numbers mt-2">
                          <Pagination
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination pagination-sm no-margin pull-right"
                            activePage={pageOther}
                            itemsCountPerPage={Number(pageSizeOther)}
                            totalItemsCount={sortedOtherTasks.length}
                            pageRangeDisplayed={5}
                            onChange={(n) => setPageOther(n)}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* YEUCAU */}
              {(filterTab === 'all' || filterTab === 'yeucau') && (
                <Row>
                  <Col md={12}>
                    <Card className="mb-4">
                      <Card.Header style={headerGridStyle}>
                        <h3 className="card-title mb-0" style={{ justifySelf: 'start' }}>
                          Xử lý yêu cầu
                        </h3>

                        <span style={totalCountStyleInline}>Tổng: {sortedYeuCauTasks.length}</span>

                        <div />
                      </Card.Header>

                      <Card.Body>
                        <div className="d-flex justify-content-between mb-2 flex-wrap">
                          <form className="form-inline mb-2">
                            <label className="mr-2">Hiển thị :</label>
                            <select
                              className="form-control"
                              value={pageSizeYeuCau}
                              onChange={(e) => {
                                const newSize = parseInt(e.target.value, 10) || 20;
                                setPageSizeYeuCau(newSize);
                                setPageYeuCau(1);
                              }}
                            >
                              {objectShowNumber.map((item, key) => (
                                <option key={key} value={item.value}>
                                  {item.text}
                                </option>
                              ))}
                            </select>
                          </form>

                          <div className="form-inline mb-2">
                            <label className="mr-2">Trạng thái:</label>
                            <select
                              className="form-control mr-2"
                              value={yeuCauStatusFilter}
                              onChange={(e) => {
                                setYeuCauStatusFilter(e.target.value);
                                setPageYeuCau(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {TRANG_THAI_YEU_CAU_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <label className="mr-2">Ưu tiên:</label>
                            <select
                              className="form-control"
                              value={yeuCauPriorityFilter}
                              onChange={(e) => {
                                setYeuCauPriorityFilter(e.target.value);
                                setPageYeuCau(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {MUC_DO_UU_TIEN.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm ml-2"
                              onClick={() => {
                                setYeuCauStatusFilter('');
                                setYeuCauPriorityFilter('');
                                setPageYeuCau(1);
                              }}
                            >
                              Xóa lọc
                            </button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <Table striped bordered hover className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th style={{ ...TABLE_HEADER_FOOTER_CELL_STYLE, width: 70 }}>STT</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Lĩnh vực</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Khoa</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ngày bắt đầu</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Dự kiến kết thúc</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ưu tiên</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Nội dung</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ticket</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Người phối hợp</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Trạng thái</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ghi chú</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedYeuCauTasks.length === 0 && (
                                <tr>
                                  <td colSpan={11} className="text-center text-muted">
                                    Không có yêu cầu.
                                  </td>
                                </tr>
                              )}

                              {pagedYeuCauTasks.map((raw, index) => {
                                const item = {
                                  id: raw.id ?? raw.Id,
                                  linhVucId: raw.linhVucId ?? raw.LinhVucId,
                                  khoaIds: raw.khoaIds ?? raw.KhoaIds,
                                  ngayBatDau: raw.ngayBatDau ?? raw.NgayBatDau,
                                  ngayHoanThanhDuKien:
                                    raw.ngayHoanThanhDuKien ?? raw.NgayHoanThanhDuKien,
                                  mucDo: raw.mucDo ?? raw.MucDo,
                                  noiDung: raw.noiDung ?? raw.NoiDung,
                                  linkTicket: raw.linkTicket ?? raw.LinkTicket,
                                  nguoiPhoiHopIds: raw.nguoiPhoiHopIds ?? raw.NguoiPhoiHopIds,
                                  status: raw.status ?? raw.Status,
                                  ghiChu: raw.ghiChu ?? raw.GhiChu,
                                };

                                return (
                                  <tr key={item.id || index}>
                                    <td className="text-center">
                                      {index + 1 + (pageYeuCau - 1) * pageSizeYeuCau}
                                    </td>
                                    <td>
                                      {getLinhVucNameById(item.linhVucId) || safeText(item.linhVucId)}
                                    </td>
                                    <td style={{ width: 110 }}>{getKhoaCodesByIds(item.khoaIds) || ''}</td>
                                    <td>{formatDate(item.ngayBatDau)}</td>
                                    <td>{formatDate(item.ngayHoanThanhDuKien)}</td>
                                    <td>{mapMucDoUuTienLabel(item.mucDo)}</td>
                                    <td style={{ minWidth: 220 }}>{safeText(item.noiDung)}</td>
                                    <td className="text-center" style={{ width: 90 }}>
                                      {renderTicketLink(item.linkTicket)}
                                    </td>
                                    <td style={{ minWidth: 180 }}>
                                      {getNguoiPhoiHopNamesByIds(item.nguoiPhoiHopIds) || '-'}
                                    </td>
                                    <td>
                                      <span style={getStatusStyleYeuCau(item.status)}>
                                        {mapTrangThaiYeuCauLabel(item.status)}
                                      </span>
                                    </td>
                                    <td style={{ minWidth: 200 }}>{safeText(item.ghiChu)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>

                        <div className="dataTables_paginate paging_simple_numbers mt-2">
                          <Pagination
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination pagination-sm no-margin pull-right"
                            activePage={pageYeuCau}
                            itemsCountPerPage={Number(pageSizeYeuCau)}
                            totalItemsCount={sortedYeuCauTasks.length}
                            pageRangeDisplayed={5}
                            onChange={(n) => setPageYeuCau(n)}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* HOTRO */}
              {(filterTab === 'all' || filterTab === 'hotro') && (
                <Row>
                  <Col md={12}>
                    <Card className="mb-4">
                      <Card.Header style={headerGridStyle}>
                        <h3 className="card-title mb-0" style={{ justifySelf: 'start' }}>
                          Hỗ trợ phần mềm
                        </h3>

                        <span style={totalCountStyleInline}>Tổng: {totalHoTroTheoKhoa}</span>

                        <div />
                      </Card.Header>

                      <Card.Body>
                        <div className="d-flex justify-content-between mb-2 flex-wrap">
                          <form className="form-inline mb-2">
                            <label className="mr-2">Hiển thị :</label>
                            <select
                              className="form-control"
                              value={pageSizeHoTro}
                              onChange={(e) => {
                                const newSize = parseInt(e.target.value, 10) || 20;
                                setPageSizeHoTro(newSize);
                                setPageHoTro(1);
                              }}
                            >
                              {objectShowNumber.map((item, key) => (
                                <option key={key} value={item.value}>
                                  {item.text}
                                </option>
                              ))}
                            </select>
                          </form>

                          <div className="form-inline mb-2">
                            <label className="mr-2">Trạng thái:</label>
                            <select
                              className="form-control mr-2"
                              value={hoTroStatusFilter}
                              onChange={(e) => {
                                setHoTroStatusFilter(e.target.value);
                                setPageHoTro(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {TRANG_THAI_HO_TRO_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <label className="mr-2">Loại lỗi:</label>
                            <select
                              className="form-control mr-2"
                              value={hoTroLoaiLoiFilter}
                              onChange={(e) => {
                                setHoTroLoaiLoiFilter(e.target.value);
                                setPageHoTro(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {LOAI_LOI_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <label className="mr-2">Mức độ:</label>
                            <select
                              className="form-control"
                              value={hoTroMucDoFilter}
                              onChange={(e) => {
                                setHoTroMucDoFilter(e.target.value);
                                setPageHoTro(1);
                              }}
                            >
                              <option value="">Tất cả</option>
                              {MUC_DO_PHUC_TAP_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm ml-2"
                              onClick={() => {
                                setHoTroStatusFilter('');
                                setHoTroLoaiLoiFilter('');
                                setHoTroMucDoFilter('');
                                setPageHoTro(1);
                              }}
                            >
                              Xóa lọc
                            </button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <Table striped bordered hover className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th style={{ ...TABLE_HEADER_FOOTER_CELL_STYLE, width: 70 }}>STT</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Lĩnh vực</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ngày hỗ trợ</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Nội dung</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Trạng thái</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Khoa</th>
                                <th style={{ ...TABLE_HEADER_FOOTER_CELL_STYLE, width: 90 }}>Số lượng</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Loại lỗi</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Mức độ</th>
                                <th style={TABLE_HEADER_FOOTER_CELL_STYLE}>Ghi chú</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedHoTroTasks.length === 0 && (
                                <tr>
                                  <td colSpan={10} className="text-center text-muted">
                                    Không có hỗ trợ phần mềm.
                                  </td>
                                </tr>
                              )}

                              {pagedHoTroTasks.map((raw, index) => {
                                const item = {
                                  linhVucId: raw.linhVucId ?? raw.LinhVucId,
                                  ngayHoTro: raw.ngayHoTro ?? raw.NgayHoTro,
                                  noiDung: raw.noiDung ?? raw.NoiDung,
                                  status: raw.status ?? raw.Status,
                                  khoaIds: raw.khoaIds ?? raw.KhoaIds,
                                  loaiLoi: raw.loaiLoi ?? raw.LoaiLoi,
                                  mucDoPhucTap: raw.mucDoPhucTap ?? raw.MucDoPhucTap,
                                  ghiChu: raw.ghiChu ?? raw.GhiChu,
                                };

                                const khoaCount = getKhoaCountByIds(item.khoaIds);

                                return (
                                  <tr key={`${item.linhVucId || ''}-${item.ngayHoTro || ''}-${index}`}>
                                    <td className="text-center">{index + 1 + (pageHoTro - 1) * pageSizeHoTro}</td>

                                    <td>{getLinhVucNameById(item.linhVucId) || safeText(item.linhVucId)}</td>

                                    <td>{formatDate(item.ngayHoTro)}</td>

                                    <td style={{ minWidth: 240 }}>{safeText(item.noiDung)}</td>

                                    <td>
                                      <span style={getStatusStyleHoTro(item.status)}>
                                        {mapTrangThaiHoTroLabel(item.status)}
                                      </span>
                                    </td>

                                    <td style={{ width: 110 }}>{getKhoaCodesByIds(item.khoaIds) || ''}</td>

                                    <td className="text-center" style={{ width: 90 }}>
                                      {khoaCount}
                                    </td>

                                    <td>{item.loaiLoi != null ? mapLoaiLoiLabel(item.loaiLoi) : ''}</td>

                                    <td>
                                      {item.mucDoPhucTap != null ? mapMucDoPhucTapLabel(item.mucDoPhucTap) : ''}
                                    </td>

                                    <td style={{ minWidth: 200 }}>{safeText(item.ghiChu)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>

                        <div className="dataTables_paginate paging_simple_numbers mt-2">
                          <Pagination
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination pagination-sm no-margin pull-right"
                            activePage={pageHoTro}
                            itemsCountPerPage={Number(pageSizeHoTro)}
                            totalItemsCount={sortedHoTroTasks.length}
                            pageRangeDisplayed={5}
                            onChange={(n) => setPageHoTro(n)}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BaoCaoChiTietNhanVien;
